import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSessionContext } from "@/contexts/SessionExpiryContext";
import { Button } from "../ui/button";
import { ShieldAlert, Clock } from "lucide-react";
import { useI18n } from "@/contexts/i18nContext";
import {
  SESSION_WARNING_TIME_SECONDS,
  TIMER_INTERVAL_MS,
} from "@/common/config/constants";

const SessionExpiryModal = () => {
  const { t } = useI18n();
  const {
    expirationTime,
    isModalOpen,
    handleExtendSession,
    isLoading,
    dismissManually,
  } = useSessionContext();
  // Formatted mm:ss string shown to user
  const [formattedTime, setFormattedTime] = useState("");
  // Progress percentage for circular timer (0–100)
  const [progress, setProgress] = useState(100);
  /**
   * Update countdown display and progress ring
   * Runs every second while modal is open
   */
  useEffect(() => {
    if (!isModalOpen) return;

    const updateFormattedTime = () => {
      const timeRemaining = Math.max(
        Math.floor(expirationTime / TIMER_INTERVAL_MS),
        0,
      );
      const minutes = Math.floor(timeRemaining / 60);
      const seconds = timeRemaining % 60;
      setFormattedTime(`${minutes}:${seconds < 10 ? "0" : ""}${seconds}`);
      setProgress((timeRemaining / SESSION_WARNING_TIME_SECONDS) * 100);
    };

    updateFormattedTime();
    const interval = setInterval(updateFormattedTime, TIMER_INTERVAL_MS);
    // Cleanup on modal close / unmount
    return () => clearInterval(interval);
  }, [expirationTime, isModalOpen]);
  // Do not render anything if modal is closed
  if (!isModalOpen) return null;
  /**
   * SVG circle math
   * r = 54 → circumference ≈ 339
   * strokeDashoffset controls visible progress
   */
  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(open) => {
        if (!open) dismissManually();
      }}
    >
      <DialogContent className="max-w-md w-full p-0 bg-gradient-to-b from-slate-50 to-white rounded-2xl shadow-2xl border-0 overflow-hidden">
        {/* Header primary bar */}
        <div className="h-1.5 bg-gradient-to-r from-primary via-primary/80 to-primary/60" />

        <div className="p-8">
          <DialogHeader className="space-y-4">
            <div className="flex justify-center">
              <div className="p-3 bg-amber-50 rounded-full ring-4 ring-amber-100">
                <ShieldAlert className="w-8 h-8 text-amber-600" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-center text-slate-800 tracking-tight">
              {t("sessionExpiryModal.title")}
            </DialogTitle>
          </DialogHeader>

          <DialogDescription className="text-center text-slate-600 mt-3 text-base leading-relaxed">
            {t("sessionExpiryModal.message")}
          </DialogDescription>

          {/* Circular Timer */}
          <div className="flex justify-center items-center mt-8 mb-6">
            <div className="relative">
              <svg className="w-32 h-32 transform -rotate-90">
                {/* Background circle */}
                <circle
                  cx="64"
                  cy="64"
                  r="54"
                  stroke="hsl(var(--primary) / 0.2)"
                  strokeWidth="8"
                  fill="none"
                />

                {/* Progress circle */}
                <circle
                  cx="64"
                  cy="64"
                  r="54"
                  stroke="url(#accentGradient)"
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-1000 ease-linear"
                />

                <defs>
                  <linearGradient
                    id="accentGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="hsl(var(--primary))" />
                    <stop offset="100%" stopColor="hsl(var(--primary) / 0.6)" />
                  </linearGradient>
                </defs>
              </svg>

              {/* Timer text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Clock className="w-4 h-4 mb-1" />
                <span className="text-3xl font-bold text-slate-800 tracking-tight font-mono">
                  {formattedTime}
                </span>
                <span className="text-xs text-slate-500">
                  {t("sessionExpiryModal.remaining")}
                </span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-3 mt-6">
            <Button
              onClick={handleExtendSession}
              disabled={isLoading}
              className="w-full py-6 rounded-xl text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25 transition-all duration-200 hover:shadow-xl hover:shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
              loading={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  {t("sessionExpiryModal.extendingButton")}...
                </span>
              ) : (
                t("sessionExpiryModal.extendButton")
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(SessionExpiryModal);
